import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  AppstoreOutlined,
  LineChartOutlined,
  FlagOutlined,
  EnvironmentOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  ContainerOutlined,
  TeamOutlined,
  // BarChartOutlined,
  WarningOutlined,
  FileTextOutlined,
  BlockOutlined,
} from '@ant-design/icons';
import { replaceSubtext } from '@/lib/utils';
import { useLoggedUser } from '@/hooks/user/useLoggedUser';

const cooperativeMenuItems = [
  {
    id: 1,
    name: 'Dashboard',
    path: '/cooperative/:entityId',
    icon: <AppstoreOutlined />,
  },
  {
    id: 2,
    name: 'Orders',
    path: '/cooperative/:entityId/orders',
    icon: <LineChartOutlined />,
  },
  {
    id: 3,
    name: 'Manage Products',
    path: '/cooperative/:entityId/products',
    icon: <ShoppingCartOutlined />,
  },
  {
    id: 4,
    name: 'Map',
    path: '/cooperative/:entityId/map',
    icon: <EnvironmentOutlined />,
  },
  {
    id: 5,
    name: 'Mission & Achievements',
    path: '/cooperative/:entityId/missions',
    icon: <FlagOutlined />,
  },
  {
    id: 6,
    name: 'Vouchers',
    path: '/cooperative/:entityId/vouchers',
    icon: <ContainerOutlined />,
  },
];

const marketMenuItems = [
  {
    id: 1,
    name: 'Dashboard',
    path: '/market/:entityId',
    icon: <AppstoreOutlined />,
  },
  {
    id: 2,
    name: 'Cooperatives',
    path: '/market/:entityId/cooperatives',
    icon: <ShopOutlined />,
  },
  {
    id: 3,
    name: 'Products',
    path: '/market/:entityId/products',
    icon: <ShoppingCartOutlined />,
  },
  {
    id: 4,
    name: 'Orders',
    path: '/market/:entityId/orders',
    icon: <ContainerOutlined />,
  },
  {
    id: 5,
    name: 'Map',
    path: '/market/:entityId/map',
    icon: <EnvironmentOutlined />,
  },
  {
    id: 6,
    name: 'Mission & Achievements',
    path: '/market/:entityId/missions',
    icon: <FlagOutlined />,
  },
  {
    id: 7,
    name: 'Notifications',
    path: '/market/:entityId/notifications',
    icon: <WarningOutlined />,
  },
];

const adminMenuItems = [
  {
    id: 1,
    name: 'Dashboard',
    path: '/admin/:entityId',
    icon: <AppstoreOutlined />,
    isSuperAdminOnly: false,
  },
  {
    id: 2,
    name: 'Users',
    path: '/admin/:entityId/users',
    icon: <TeamOutlined />,
    isSuperAdminOnly: true,
  },
  {
    id: 3,
    name: 'Orders',
    path: '/admin/:entityId/orders',
    icon: <ContainerOutlined />,
    isSuperAdminOnly: false,
  },
  {
    id: 4,
    name: 'Cooperatives',
    path: '/admin/:entityId/cooperatives',
    icon: <ShopOutlined />,
    isSuperAdminOnly: false,
  },
  {
    id: 5,
    name: 'Markets',
    path: '/admin/:entityId/markets',
    icon: <ContainerOutlined />,
    isSuperAdminOnly: false,
  },
  // Hidden menu items
  // {
  //   id: 6,
  //   name: 'Products',
  //   path: '/admin/:entityId/products',
  //   icon: <ShoppingCartOutlined />,
  //   isSuperAdminOnly: true,
  // },
  // {
  //   id: 7,
  //   name: 'Performance',
  //   path: '/admin/:entityId/performance',
  //   icon: <BarChartOutlined />,
  //   isSuperAdminOnly: false,
  // },
  // {
  //   id: 8,
  //   name: 'Map',
  //   path: '/admin/:entityId/map',
  //   icon: <EnvironmentOutlined />,
  //   isSuperAdminOnly: false,
  // },
  // {
  //   id: 9,
  //   name: 'Mission & Achievements',
  //   path: '/admin/:entityId/mission-and-rewards',
  //   icon: <FlagOutlined />,
  //   isSuperAdminOnly: false,
  // },
  /*{
    id: 10,
    name: 'Reports',
    path: '/admin/:entityId/reports',
    icon: <WarningOutlined />,
    isSuperAdminOnly: true,
  },*/
  {
    id: 11,
    name: 'News',
    path: '/admin/:entityId/news',
    icon: <FileTextOutlined />,
    isSuperAdminOnly: false,
  },
  {
    id: 12,
    name: 'History',
    path: '/admin/:entityId/blockchain',
    icon: <BlockOutlined />,
    isSuperAdminOnly: false,
  },
];

const getAdminMenuItems = (isSuperAdmin: boolean) => {
  if (isSuperAdmin) {
    return adminMenuItems;
  }

  return adminMenuItems.filter(item => !item.isSuperAdminOnly);
};

const menuItemMap = {
  COOPERATIVE: cooperativeMenuItems,
  ADMIN: getAdminMenuItems(false),
  SUPER_ADMIN: getAdminMenuItems(true),
  MARKET: marketMenuItems,
};

const MenuItems: React.FC = () => {
  const { userType, entityId } = useLoggedUser();

  if (!userType) {
    return null;
  }

  const navItems = menuItemMap[userType as keyof typeof menuItemMap] ?? [];
  return (
    <>
      <ul>
        {navItems.map(navItem => (
          <li key={navItem.id} className="px-4 w-full mb-1">
            <NavLink
              key={navItem.id}
              to={replaceSubtext(navItem.path, ':entityId', entityId)}
              end={true}
              className={({ isActive }) =>
                `px-4 py-2 w-full block rounded-md text-sm font-medium text-black ${
                  isActive
                    ? 'bg-green-800 text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                }`
              }
            >
              <div className="flex gap-2">
                {navItem.icon}
                <span>{navItem.name}</span>
              </div>
            </NavLink>
          </li>
        ))}
      </ul>
    </>
  );
};

export default MenuItems;
